import { ModuleDocContent } from "./docsData";

export const authModuleDocs: Record<string, ModuleDocContent> = {
  "auth-login": {
    title: "Login",
    subtitle: "User Authentication",
    color: "blue",
    badge: "Authentication",
    description:
      "The Login page is the entry point to the SkillZen platform where users authenticate with their credentials or SSO providers.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>Login</strong> page allows users to sign in using their email and password, or through configured SSO providers such as Google, GitHub, Microsoft, and Okta.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/534f7197-ff8e-42b7-b01f-42b9e5023290.png" alt="Login Page" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Login Page</p></div>

      <section>
        <h2>Login Form Fields</h2>
        <p>The login form requires the following credentials to authenticate:</p>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Email</strong></td>
              <td>Your registered email address. Validated for correct email format.</td>
            </tr>
            <tr>
              <td><strong>Password</strong></td>
              <td>Your account password. A visibility toggle lets you show or hide the entered text.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>SSO Login</h2>
        <p>If your organization has configured Single Sign-On, SSO provider buttons appear below the login form. Clicking a provider redirects you to authenticate through that identity provider and returns you to the platform automatically.</p>
      </section>

      <section>
        <h2>Additional Actions</h2>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Forgot password?</strong></td>
              <td>Navigates to the password reset flow to recover your account.</td>
            </tr>
            <tr>
              <td><strong>Register</strong></td>
              <td>Navigates to the registration page if self-registration is enabled for your organization.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Account Status Handling</h2>
        <p>The login page checks your account status and shows appropriate messages if your account is inactive, suspended, locked, or archived. If the organization tenant is blocked, you will be redirected to a tenant status page.</p>
      </section>

    </div>`,
  },

  "auth-register": {
    title: "Register",
    subtitle: "New Account Registration",
    color: "blue",
    badge: "Authentication",
    description:
      "The Register page allows new users to create an account through self-registration or invitation-based signup.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>Register</strong> page supports two registration flows: <strong>self-registration</strong> (if enabled by your organization) and <strong>invitation-based registration</strong> where form fields are pre-filled from the invitation data.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/f7c46d02-7f44-4cab-ac07-54a3439d4c06.png" alt="Register Page" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Register Page</p></div>

      <section>
        <h2>Registration Form Fields</h2>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>First Name</strong></td>
              <td>Your first name (required).</td>
            </tr>
            <tr>
              <td><strong>Last Name</strong></td>
              <td>Your last name (required).</td>
            </tr>
            <tr>
              <td><strong>Email</strong></td>
              <td>A valid email address. Real-time availability check ensures the email is not already registered.</td>
            </tr>
            <tr>
              <td><strong>Password</strong></td>
              <td>Must meet strength requirements: minimum 8 characters, at least one uppercase letter, one lowercase letter, and one number. A strength indicator shows progress.</td>
            </tr>
            <tr>
              <td><strong>Confirm Password</strong></td>
              <td>Must match the password field.</td>
            </tr>
            <tr>
              <td><strong>Phone Number</strong></td>
              <td>Optional. International phone format supported.</td>
            </tr>
            <tr>
              <td><strong>Employee ID</strong></td>
              <td>Optional. Your organization employee identifier.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Registration Flows</h2>
        <table>
          <thead>
            <tr>
              <th>Flow</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Self-Registration</strong></td>
              <td>Available when enabled by the organization. After submitting, a verification email is sent. You must verify your email before logging in.</td>
            </tr>
            <tr>
              <td><strong>Invitation-Based</strong></td>
              <td>Accessed via an invitation link. Name and email are pre-filled from the invitation. After registration, you can log in directly without email verification.</td>
            </tr>
          </tbody>
        </table>
      </section>

    </div>`,
  },

  "auth-forgot-password": {
    title: "Forgot Password",
    subtitle: "Password Recovery",
    color: "blue",
    badge: "Authentication",
    description:
      "The Forgot Password page lets users request a password reset link to recover access to their account.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>Forgot Password</strong> page allows you to request a password reset link by entering your registered email address. A reset link valid for 1 hour will be sent to your inbox.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/d5096056-53f6-4746-a683-ffcafd9f5fe5.png" alt="Forgot Password Page" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Forgot Password Page</p></div>

      <section>
        <h2>How It Works</h2>
        <div class="sz-steps">
          <div class="sz-step">
            <div class="sz-step-num">1</div>
            <div>
              <div class="sz-step-title">Enter your email</div>
              <div class="sz-step-desc">Type the email address associated with your account and click <strong>Send Reset Link</strong>.</div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">2</div>
            <div>
              <div class="sz-step-title">Check your inbox</div>
              <div class="sz-step-desc">A confirmation screen appears showing the email address. The reset link is valid for <strong>1 hour</strong>.</div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">3</div>
            <div>
              <div class="sz-step-title">Reset your password</div>
              <div class="sz-step-desc">Click the link in your email to set a new password. If the email doesn't arrive, use the <strong>Resend</strong> option or check your spam folder.</div>
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>For security, the same confirmation message is shown whether or not the email exists in the system. Check your spam folder if you don't see the email within a few minutes.</p>
      </div>

    </div>`,
  },

  "error-401": {
    title: "401 — Unauthorized",
    subtitle: "Authentication Required",
    color: "red",
    badge: "Error Pages",
    description:
      "The 401 page appears when you try to access a resource without being logged in or with an expired session.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>401 Unauthorized</strong> page is shown when you attempt to access a protected page without valid authentication. This typically happens when your session has expired or you haven't logged in.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c2a65853-d2d0-42cd-8b01-4e5ca886ecf8.png" alt="401 Unauthorized Page" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: 401 Unauthorized Page</p></div>

      <section>
        <h2>When This Page Appears</h2>
        <ul class="sz-list">
          <li><strong>Session expired</strong> — Your login session timed out due to inactivity.</li>
          <li><strong>Not logged in</strong> — You navigated to a protected URL without being authenticated.</li>
          <li><strong>Token invalid</strong> — Your authentication token was revoked or is no longer valid.</li>
        </ul>
      </section>

      <section>
        <h2>Available Actions</h2>
        <table>
          <thead>
            <tr>
              <th>Button</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Login</strong></td>
              <td>Redirects you to the login page to re-authenticate.</td>
            </tr>
            <tr>
              <td><strong>Go back</strong></td>
              <td>Returns to the previous page in your browser history.</td>
            </tr>
          </tbody>
        </table>
      </section>

    </div>`,
  },

  "error-403": {
    title: "403 — Access Denied",
    subtitle: "Insufficient Permissions",
    color: "red",
    badge: "Error Pages",
    description:
      "The 403 page appears when you are logged in but do not have the required permissions to access a resource.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>403 Access Denied</strong> page is shown when your account lacks the necessary role or permission to view the requested page. You are authenticated but not authorized for this action.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/50ecf544-5a51-4433-81dd-6d40c3c3754c.png" alt="403 Access Denied Page" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: 403 Access Denied Page</p></div>

      <section>
        <h2>When This Page Appears</h2>
        <ul class="sz-list">
          <li><strong>Missing role</strong> — Your user role does not include access to the requested module.</li>
          <li><strong>Permission not granted</strong> — The specific permission required for this page has not been assigned to your role.</li>
        </ul>
      </section>

      <section>
        <h2>Available Actions</h2>
        <table>
          <thead>
            <tr>
              <th>Button</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Back</strong></td>
              <td>Returns to the home page.</td>
            </tr>
            <tr>
              <td><strong>Logout</strong></td>
              <td>Logs you out so you can sign in with a different account that has the required permissions.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>If you believe you should have access, contact your administrator to review the role and permission assignments for your account.</p>
      </div>

    </div>`,
  },

  "error-404": {
    title: "404 — Page Not Found",
    subtitle: "Page Does Not Exist",
    color: "red",
    badge: "Error Pages",
    description:
      "The 404 page appears when you navigate to a URL that does not match any existing route in the application.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>404 Not Found</strong> page is displayed when the URL you visited does not correspond to any page in the platform. This could be due to a mistyped URL, a removed page, or an outdated bookmark.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/35c1320f-d840-4763-ae91-a2d158f0b205.png" alt="404 Not Found Page" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: 404 Not Found Page</p></div>

      <section>
        <h2>When This Page Appears</h2>
        <ul class="sz-list">
          <li><strong>Mistyped URL</strong> — The address entered in the browser does not match any route.</li>
          <li><strong>Page removed or moved</strong> — The page you're looking for may have been deleted or its URL changed.</li>
          <li><strong>Outdated bookmark</strong> — A saved link points to a route that no longer exists.</li>
        </ul>
      </section>

      <section>
        <h2>Available Actions</h2>
        <table>
          <thead>
            <tr>
              <th>Button</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Dashboard</strong></td>
              <td>Navigates to the admin dashboard home page.</td>
            </tr>
            <tr>
              <td><strong>Go back</strong></td>
              <td>Returns to the previous page in your browser history.</td>
            </tr>
          </tbody>
        </table>
      </section>

    </div>`,
  },
};
